/**
 * Flex / Stack — lightweight layout primitives for the prince-ui migration.
 *
 * Replaces the legacy UI5 FlexBox without pulling in prince-ui
 * (which has no FlexBox equivalent). Props mirror the UI5 FlexBox surface
 * actually used in this repo:
 *
 *   direction      "Row" | "Column"  (UI5 FlexBoxDirection values accepted)
 *   justifyContent CSS value OR UI5 enum string (e.g. "SpaceBetween")
 *   alignItems     CSS value OR UI5 enum string (e.g. "Center")
 *   wrap           boolean | "Wrap" | "NoWrap" (UI5 FlexBoxWrap)
 *   gap            CSS gap value (string or number; number → px)
 *   className      extra classes
 *   style          inline style overrides (merged last, wins)
 *   children       ReactNode
 *
 * Plain CSS values pass through unchanged so callers can migrate prop-by-prop.
 * `Stack` is Flex with direction="column" pre-set.
 */

import { type CSSProperties, type ReactNode } from "react";
import "./kit.css";

// ── UI5 → CSS adapters ────────────────────────────────────────────────────

const DIRECTION_MAP: Record<string, CSSProperties["flexDirection"]> = {
  Row: "row",
  Column: "column",
  RowReverse: "row-reverse",
  ColumnReverse: "column-reverse",
};

const JUSTIFY_MAP: Record<string, CSSProperties["justifyContent"]> = {
  Start: "flex-start",
  End: "flex-end",
  Center: "center",
  SpaceBetween: "space-between",
  SpaceAround: "space-around",
  SpaceEvenly: "space-evenly",
};

const ALIGN_MAP: Record<string, CSSProperties["alignItems"]> = {
  Start: "flex-start",
  End: "flex-end",
  Center: "center",
  Baseline: "baseline",
  Stretch: "stretch",
};

function toDirection(v?: string): CSSProperties["flexDirection"] {
  if (!v) return undefined;
  return DIRECTION_MAP[v] ?? (v as CSSProperties["flexDirection"]);
}

function toJustify(v?: string): CSSProperties["justifyContent"] {
  if (!v) return undefined;
  return JUSTIFY_MAP[v] ?? (v as CSSProperties["justifyContent"]);
}

function toAlign(v?: string): CSSProperties["alignItems"] {
  if (!v) return undefined;
  return ALIGN_MAP[v] ?? (v as CSSProperties["alignItems"]);
}

function toWrap(v?: boolean | string): CSSProperties["flexWrap"] {
  if (v === undefined || v === null) return undefined;
  if (v === true || v === "Wrap") return "wrap";
  if (v === false || v === "NoWrap") return "nowrap";
  return v as CSSProperties["flexWrap"];
}

function toGap(v?: string | number): CSSProperties["gap"] {
  if (v === undefined) return undefined;
  return typeof v === "number" ? `${v}px` : v;
}

// ── Props ─────────────────────────────────────────────────────────────────

export interface FlexProps {
  /** flex-direction. Accepts UI5 enum values ("Row" | "Column") or plain CSS. */
  direction?: "Row" | "Column" | "RowReverse" | "ColumnReverse" | string;
  /** justify-content. Accepts UI5 enum values ("SpaceBetween" etc.) or plain CSS. */
  justifyContent?: string;
  /** align-items. Accepts UI5 enum values ("Center" etc.) or plain CSS. */
  alignItems?: string;
  /** flex-wrap. Pass true/false, UI5 "Wrap"/"NoWrap", or plain CSS. */
  wrap?: boolean | string;
  /** gap (CSS shorthand). Numbers are treated as px. */
  gap?: string | number;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

// ── Components ────────────────────────────────────────────────────────────

/**
 * `<Flex>` — a `div` with `display: flex`.
 * All layout props map to inline CSS; `style` wins over derived values.
 */
export function Flex({
  direction,
  justifyContent,
  alignItems,
  wrap,
  gap,
  className,
  style,
  children,
}: FlexProps) {
  const derived: CSSProperties = {
    flexDirection: toDirection(direction),
    justifyContent: toJustify(justifyContent),
    alignItems: toAlign(alignItems),
    flexWrap: toWrap(wrap),
    gap: toGap(gap),
  };

  // Merge: derived first, caller style overrides
  const merged: CSSProperties = { ...derived, ...style };

  return (
    <div
      className={["prn-flex", className].filter(Boolean).join(" ")}
      style={merged}
    >
      {children}
    </div>
  );
}

/**
 * `<Stack>` — `Flex` with `direction="column"` pre-set.
 * Shorthand for vertical stacking.
 */
export function Stack(props: Omit<FlexProps, "direction"> & { direction?: "Column" | "ColumnReverse" }) {
  return <Flex {...props} direction={props.direction ?? "Column"} />;
}
